import cn from 'classnames';
import { ChangeEvent, FC, KeyboardEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Checkbox } from 'src/components/common/Checkbox';
import { Input } from 'src/components/common/Input';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { getStringWithoutSpace, invalidKeys } from 'src/helpers/utils';
import { RootState } from 'src/types/store-types';
import { COUNTRY_LIST } from 'src/utils/constants';
import { getValidationSchemas } from './accountDetailValidationScheme';
import _styles from './styles.module.scss';

type Props = {
  title: string;
  value: string | number | null;
  isEditable?: boolean;
  updateField?: any;
  fieldName: string;
  emailVerifed?: boolean;
  phoneVerified?: boolean;
};

const AccountDetail: FC<Props> = ({
  title,
  value,
  isEditable,
  updateField,
  fieldName,
  emailVerifed,
  phoneVerified,
}) => {
  const { t }: any = useTranslation();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isRemovedPhoneCode, setIsRemovedPhoneCode] = useState<boolean>(true);
  const [fieldValue, setFieldValue] = useState<string | number | null | boolean>(value);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user, phoneCode } = useSelector((state: RootState) => state.user);
  const showEmailIcon = title.includes('email') && process.env.REACT_APP_EMAIL_VERIFICATION === 'true';
  const showPhoneIcon = title.includes('phoneNumber') && process.env.REACT_APP_PHONE_NUMBER_VERIFICATION === 'true';

  const getPhoneCodeFromPersonalInfo = useMemo(() => {
    return COUNTRY_LIST.find((country) => country.value === user?.personalInfo?.country)?.code as string;
  }, [user?.personalInfo?.country]);

  const handleValidate = async (fieldValue: string | number | boolean): Promise<void> => {
    const schema = getValidationSchemas(t, getPhoneCodeFromPersonalInfo || phoneCode);
    if (!schema) return;

    try {
      await schema[fieldName].validate(fieldValue);
      setErrorMessage('');
    } catch (error: any) {
      setErrorMessage(error.message || t('invalidValue'));
    }
  };

  const inputType = useMemo(() => {
    if (fieldName === 'phoneNumber') return 'number';
    return 'text';
  }, [fieldName]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (invalidKeys.includes(e.key) && e.currentTarget.type === 'number') {
      e.preventDefault();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setIsRemovedPhoneCode(false);
    const newValue = e.target.value;
    setFieldValue(newValue);
    handleValidate(newValue).catch((e) => console.log(e));
  };

  const handleChangeCheckbox = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.checked;
    setFieldValue(newValue);
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>): void => {
    setIsRemovedPhoneCode(false);
    setFieldValue(e.target.value);
    handleValidate(e.target.value).catch((e) => console.log(e));
  };

  const handleClickOnEdit = (): void => {
    handleValidate(inputValue()).catch((e) => console.log(e));
    setIsEditMode(true);
  };

  const handleCancel = (): void => {
    setIsRemovedPhoneCode(true);
    setIsEditMode(false);
    setFieldValue(value);
    setErrorMessage('');
  };

  const onUpdateField = (fieldName: string, fieldValue: string | number | boolean): void => {
    const code = getPhoneCodeFromPersonalInfo || phoneCode;
    const isChanged = fieldName === 'phoneNumber' ? '+' + value !== code + fieldValue : value !== fieldValue;
    if (fieldName === 'email') {
      fieldValue = getStringWithoutSpace(fieldValue as string);
    }
    if ((!errorMessage || typeof fieldValue === 'boolean') && isChanged) {
      setIsRemovedPhoneCode(true);
      updateField(fieldName, fieldValue);
    } else {
      setIsEditMode(false);
    }
  };

  const getVerificationIcon = (isVerified: boolean): JSX.Element => (
    <SvgIcon src={isVerified ? icons.verifed : icons.unverifed} className={_styles.verifed} />
  );

  const inputValue = (): string | number | boolean => {
    const digits = (getPhoneCodeFromPersonalInfo || phoneCode)?.replace(/\D/g, '') ?? '';
    if (
      fieldName === 'phoneNumber' &&
      isRemovedPhoneCode &&
      typeof fieldValue === 'string' &&
      fieldValue &&
      fieldValue.startsWith(digits)
    ) {
      return fieldValue.slice(digits.length);
    } else {
      return fieldValue ?? '';
    }
  };

  const phoneNumberInputCode = useMemo(() => {
    if (fieldName === 'phoneNumber' && isEditMode) {
      return (
        <Input
          name="phone-code"
          value={getPhoneCodeFromPersonalInfo || phoneCode}
          type="text"
          disabled
          className={_styles.phone_code}
          onChange={(e) => console.log(e)}
        />
      );
    }
    return null;
  }, [fieldName, isEditMode, phoneCode]);

  const isBoolean = typeof inputValue() === 'boolean';
  const valueToDisplay = isBoolean ? Boolean(inputValue()) : value || '-';

  const renderEditMode = (): JSX.Element => {
    if (isBoolean) {
      return (
        <Checkbox
          containerClassName={_styles.checkbox_edit_mode}
          onChange={handleChangeCheckbox}
          id={fieldName}
          name={title}
          label={t(title)}
          value={Boolean(valueToDisplay)}
        />
      );
    }

    return (
      <>
        {phoneNumberInputCode}
        <Input
          name={title}
          value={inputValue() as string | number}
          size="md"
          onChange={handleChange}
          type={inputType}
          onBlur={handleBlur}
          keyDown={handleKeyDown}
          error={errorMessage}
          className={_styles.edit_input}
          errorClassName={_styles.edit_input_error}
        />
      </>
    );
  };

  const renderViewMode = (): JSX.Element => {
    return (
      <span className={_styles.value}>
        {isBoolean ? (
          <Checkbox
            containerClassName={_styles.checkbox_view_mode}
            disabled
            name={title}
            value={Boolean(valueToDisplay)}
            label={t(title)}
          />
        ) : (
          valueToDisplay
        )}
      </span>
    );
  };

  return (
    <div className={_styles.detail_container}>
      <span className={_styles.title}>
        {!isBoolean && t(title)}
        {showEmailIcon && getVerificationIcon(Boolean(emailVerifed))}
        {showPhoneIcon && getVerificationIcon(Boolean(phoneVerified))}
      </span>
      {isEditable ? (
        <div className={_styles.value_container}>
          {isEditMode ? renderEditMode() : renderViewMode()}
          {isEditMode ? (
            <div className={_styles.edit_icons_wrapper}>
              <SvgIcon
                className={cn(_styles.icon, _styles.success, {
                  [_styles.error]: errorMessage && !isBoolean,
                })}
                src={icons.success}
                onClick={() => onUpdateField(fieldName, inputValue())}
              />
              <SvgIcon className={cn(_styles.icon, _styles.cancel)} src={icons.close} onClick={handleCancel} />
            </div>
          ) : (
            <SvgIcon className={_styles.icon} src={icons.edit} onClick={handleClickOnEdit} />
          )}
        </div>
      ) : (
        <div className={_styles.detail_container}>
          <span className={_styles.value}>{value || '-'}</span>
        </div>
      )}
    </div>
  );
};
export default AccountDetail;
